/**
 * ATL-1143 — the two shapes a COMPLEX SALE status can arrive in, in one place.
 *
 * Firestore holds a status OBJECT, built by `ComplexSaleDetailScreen` from its `STATUSES` table
 * with the label translated at write time:
 *
 *     complexSales.status = { id: 'finished', isActive: false, isSuccess: true, text: 'Завершен' }
 *
 * The Supabase read path returns the same object (the column is `jsonb` as of
 * `20260905120000_atl1143_complex_sale_status_jsonb.sql`), but it can also return a BARE STATUS ID
 * — a JSON string — and unlike `orders` that is the COMMON case rather than the rare one: the
 * migration transform never wrote this column at all, so every migrated row took its
 * `DEFAULT 'new'`, and the pre-ATL-1143 create/update paths reduced the object to `status.id`.
 *
 * Both shapes are permanent and both must render. What must NEVER happen is the reverse: this
 * screen seeding its form from a value and writing that value back. `ComplexSaleDetailScreen` does
 * exactly that — `saveStatus` writes `status` and the save path writes `isActive: status.isActive`
 * — so a bare string reaching `status` state would write `isActive: undefined` into Firestore, and
 * a fabricated `{id, isActive: false}` would write a status the merchant never chose. Firestore is
 * the rollback copy, so both are permanent.
 *
 * The generic half is shared with ATL-1103 and imported from `./orderStatus`; only the
 * complex-sale STATUS TABLE belongs here. Mirrors
 * `apps/atlas-backend/src/common/utils/complex-sale-status.util.ts`. Keep the two in step.
 */

import {
  isStatusMap,
  resolveStatusOption,
  statusDisplayText,
  statusForWrite,
  statusId,
  type StatusLike,
  type StatusOption,
} from './orderStatus'

export { resolveStatusOption, statusDisplayText, statusForWrite, statusId }
export type { StatusLike, StatusOption }

/**
 * The complex-sale status table — the single definition `ComplexSaleDetailScreen`, the list screen
 * and the backend's `COMPLEX_SALE_STATUSES` all agree with.
 *
 * A factory rather than a constant because `text` is TRANSLATED at write time (the screen builds
 * it as `t('complexSales.' + id)`), so it depends on the active locale. That is also why the
 * BACKEND table carries no `text`: there is no server-side spelling of it, and inventing one is
 * the defect this ticket is about.
 */
export const complexSaleStatuses = (t: (key: string) => string): StatusOption[] => [
  { id: 'new', text: t('complexSales.new'), isActive: true, isSuccess: false },
  { id: 'inProgress', text: t('complexSales.inProgress'), isActive: true, isSuccess: false },
  { id: 'finished', text: t('complexSales.finished'), isActive: false, isSuccess: true },
  { id: 'canceled', text: t('complexSales.canceled'), isActive: false, isSuccess: false },
]

/**
 * The id → `isActive` table, locale-independent — the ids and their meaning do not change with the
 * language, only the label does. Carries the legacy `active` alias the backend table also keeps, so
 * an id that used to resolve still does.
 *
 * An ARRAY searched with `.find()`, mirroring `COMPLEX_SALE_STATUSES` in the backend util, for the
 * reason given at the lookup below.
 */
const COMPLEX_SALE_ACTIVE_BY_ID: readonly { id: string; isActive: boolean }[] = [
  { id: 'new', isActive: true },
  { id: 'inProgress', isActive: true },
  { id: 'finished', isActive: false },
  { id: 'canceled', isActive: false },
  { id: 'active', isActive: true },
]

/**
 * Is this complex sale still active?
 *
 * The map's own `isActive` wins where it exists — it is what the till wrote. Otherwise the id
 * decides, using the same table above. `null` when the status names no known id and carries no
 * flag: `complex_sales` has no `is_active` column, so this is derived on every read, and answering
 * `false` for a merchant-defined status would hide a live job from the list screen's default
 * filter — an absent field asserting its opposite (ATL-1020).
 *
 * Mirrors `complexSaleIsActive` in the backend util; the two must agree.
 */
export const complexSaleIsActive = (status: StatusLike): boolean | null => {
  if (isStatusMap(status) && typeof status.isActive === 'boolean') return status.isActive
  const id = statusId(status)
  if (id === null) return null
  // `.find()` and not an object index: indexing a plain object with an id naming an
  // `Object.prototype` member returns a FUNCTION (`'constructor'`, `'toString'`) or the prototype
  // (`'__proto__'`), not `undefined` — so `?? null` never fires and the save path would put a
  // function into the Firestore payload, which the SDK rejects. It also broke the "the two must
  // agree for every id" invariant this file claims: the backend already searches an array.
  return COMPLEX_SALE_ACTIVE_BY_ID.find((s) => s.id === id)?.isActive ?? null
}
